/*----------------------------------
- DEPENDANCES
----------------------------------*/

import React from 'react';

/*----------------------------------
- APP
----------------------------------*/

import Motion, { fadeIn } from '@/client/components/Motion';
import { CLIENT_LOGOS } from '@/client/catalogs/marketing/clientLogos';

/*----------------------------------
- TYPES
----------------------------------*/

export interface ClientLogosCarouselProps {
    className?: string;
    headline?: string;
}

/*----------------------------------
- COMPONENT
----------------------------------*/

export default function ClientLogosCarousel({
    className = '',
    headline = 'Trusted by 800+ Companies Worldwide',
}: ClientLogosCarouselProps) {
    const [currentLogoIndex, setCurrentLogoIndex] = React.useState(0);
    const visibleLogos = React.useMemo(
        () =>
            Array.from({ length: 6 }, (_, index) => CLIENT_LOGOS[(currentLogoIndex + index) % CLIENT_LOGOS.length]),
        [currentLogoIndex],
    );

    React.useEffect(() => {
        const intervalId = window.setInterval(() => {
            setCurrentLogoIndex((prev) => (prev + 1) % CLIENT_LOGOS.length);
        }, 3000);

        return () => {
            window.clearInterval(intervalId);
        };
    }, []);

    return (
        <section className={`py-16 px-6 lg:px-8 bg-white border-y border-slate-200 ${className}`}>
            <div className="max-w-7xl mx-auto">
                <Motion
                    tag="h3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{
                        once: true,
                    }}
                    variants={fadeIn}
                    className="text-center text-slate-600 font-semibold mb-8 uppercase tracking-wider text-sm"
                >
                    {headline}
                </Motion>

                <div className="relative overflow-hidden">
                    <div className="flex items-center justify-center gap-12 flex-wrap md:flex-nowrap">
                        {visibleLogos.map((logo, index) => (
                            <Motion
                                tag="div"
                                key={`${currentLogoIndex}-${logo}-${index}`}
                                initial={{
                                    opacity: 0,
                                    x: 80,
                                }}
                                animate={{
                                    opacity: 1,
                                    x: 0,
                                }}
                                transition={{
                                    duration: 0.45,
                                    delay: index * 0.08,
                                }}
                                className="flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300 flex-shrink-0"
                            >
                                <div className="h-12 flex items-center justify-center px-6">
                                    <span className="text-slate-400 font-bold text-xl">{logo}</span>
                                </div>
                            </Motion>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
